import React, { useState } from 'react';
import { TrendingUpIcon, CalendarIcon, ActivityIcon } from 'lucide-react';
export function SuccessRateSection() {
  const [selectedType, setSelectedType] = useState<'self' | 'donor'>('self');
  const selfEggsData = [{
    ageRange: '<35 years',
    rate: 54,
    color: 'bg-emerald-500'
  }, {
    ageRange: '35 – 40 years',
    rate: 47,
    color: 'bg-blue-500'
  }, {
    ageRange: '>40 years',
    rate: 26,
    color: 'bg-amber-500'
  }, {
    ageRange: '>40 with PGT',
    rate: 72,
    color: 'bg-purple-500'
  }];
  const donorEggsData = [{
    ageRange: '<35 years',
    rate: 78,
    color: 'bg-emerald-500'
  }, {
    ageRange: '35 – 40 years',
    rate: 75,
    color: 'bg-blue-500'
  }, {
    ageRange: '>40 years',
    rate: 68,
    color: 'bg-amber-500'
  }, {
    ageRange: '>40 with PGT',
    rate: 85,
    color: 'bg-purple-500'
  }];
  const cycleTypeData = [{
    type: 'Donated Eggs',
    rate: 69,
    color: 'from-cyan-500 to-cyan-600'
  }, {
    type: 'Donated Embryos',
    rate: 80,
    color: 'from-blue-500 to-blue-600'
  }];
  const currentData = selectedType === 'self' ? selfEggsData : donorEggsData;
  return <section className="py-20 bg-gradient-to-b from-white to-slate-50" data-id="element-709">
      <div className="container mx-auto px-4" data-id="element-710">
        <div className="text-center mb-12" data-id="element-711">
          <div className="inline-block bg-blue-50 rounded-full px-4 py-2 mb-4" data-id="element-712">
            <span className="text-blue-700 font-semibold text-sm tracking-wide uppercase" data-id="element-713">
              Partner Clinic Success Rates
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-normal" data-id="element-714">
            Clinical{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600" data-id="element-715">
              Success Data
            </span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg font-light leading-relaxed" data-id="element-716">
            Historical outcomes from our partner fertility clinics
          </p>
        </div>
        {/* Toggle Selector */}
        <div className="flex justify-center mb-12" data-id="element-717">
          <div className="inline-flex bg-white border-2 border-slate-200 rounded-full p-1 shadow-sm" data-id="element-718">
            <button onClick={() => setSelectedType('self')} className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${selectedType === 'self' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-719">
              Self Eggs
            </button>
            <button onClick={() => setSelectedType('donor')} className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${selectedType === 'donor' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-720">
              Donor Eggs
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto space-y-8" data-id="element-721">
          {/* Main Content Card - Age-Based Success Rates */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden" data-id="element-722">
            <div className="p-8 md:p-12" data-id="element-723">
              {selectedType === 'self' && <div className="mb-12" data-id="element-724">
                  <div className="flex items-start gap-4 mb-6" data-id="element-725">
                    <div className="bg-blue-50 p-3 rounded-xl" data-id="element-726">
                      <TrendingUpIcon className="text-blue-600" size={28} data-id="element-727" />
                    </div>
                    <div data-id="element-728">
                      <h3 className="text-3xl font-bold text-slate-900 mb-3" data-id="element-729">
                        High Success Rates Within 4 Treatment Cycles
                      </h3>
                      <p className="text-slate-600 text-lg leading-relaxed" data-id="element-730">
                        Through personalized treatment plans and advanced
                        reproductive techniques, our partner clinics achieve
                        strong success rates within 4 treatment cycles. Success
                        requires commitment, transparency, and collaborative
                        trust between you and our medical team. While success
                        rates naturally vary with age, advanced options like
                        preimplantation genetic testing (PGT) can significantly
                        enhance outcomes. The chart below displays our average
                        pregnancy success rates per cycle.
                      </p>
                    </div>
                  </div>
                </div>}
              {selectedType === 'donor' && <div className="mb-12" data-id="element-731">
                  <div className="flex items-start gap-4 mb-6" data-id="element-732">
                    <div className="bg-blue-50 p-3 rounded-xl" data-id="element-733">
                      <TrendingUpIcon className="text-blue-600" size={28} data-id="element-734" />
                    </div>
                    <div data-id="element-735">
                      <h3 className="text-3xl font-bold text-slate-900 mb-3" data-id="element-736">
                        Enhanced Success with Donor Programs
                      </h3>
                      <p className="text-slate-600 text-lg leading-relaxed" data-id="element-737">
                        Donor-assisted cycles consistently deliver higher
                        success rates across all age demographics. Our
                        rigorously vetted donor program, paired with
                        cutting-edge laboratory methods and individualized
                        treatment protocols, optimizes your path to a successful
                        pregnancy at any stage of life.
                      </p>
                    </div>
                  </div>
                </div>}
              {/* Success Rate Stats by Age */}
              <div className="mb-8" data-id="element-738">
                <div className="flex items-center gap-2 mb-6" data-id="element-739">
                  <CalendarIcon className="text-blue-600" size={20} data-id="element-740" />
                  <h4 className="text-xl font-bold text-slate-900" data-id="element-741">
                    Success Rates by Patient Age
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-742">
                  {currentData.map((item, index) => <div key={index} className="bg-slate-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300" data-id="element-743">
                      <div className="flex items-center justify-between mb-4" data-id="element-744">
                        <span className="text-slate-700 font-semibold text-lg" data-id="element-745">
                          {item.ageRange}
                        </span>
                        <span className="text-4xl font-bold text-slate-900" data-id="element-746">
                          {item.rate}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden" data-id="element-747">
                        <div className={`${item.color} h-full rounded-full transition-all duration-1000 ease-out`} style={{
                      width: `${item.rate}%`
                    }} data-id="element-748"></div>
                      </div>
                    </div>)}
                </div>
              </div>
              {/* CTA */}
              <div className="text-center pt-8 border-t border-slate-100" data-id="element-749">
                <a href="/contact" className="inline-block bg-blue-600 text-white px-8 py-4 rounded-full hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-600/30 hover:scale-105 font-semibold" data-id="element-750">
                  Book a Consultation
                </a>
              </div>
            </div>
          </div>
          {/* New Section - Success by Cycle Type */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden" data-id="element-751">
            <div className="p-8 md:p-12" data-id="element-752">
              <div className="mb-8" data-id="element-753">
                <div className="flex items-start gap-4 mb-6" data-id="element-754">
                  <div className="bg-cyan-50 p-3 rounded-xl" data-id="element-755">
                    <ActivityIcon className="text-cyan-600" size={28} data-id="element-756" />
                  </div>
                  <div data-id="element-757">
                    <h3 className="text-3xl font-bold text-slate-900 mb-3" data-id="element-758">
                      Exceptional Results Across Treatment Types
                    </h3>
                    <p className="text-slate-600 text-lg leading-relaxed" data-id="element-759">
                      Through our personalized approach and combination of
                      cutting-edge reproductive methods, our partner clinics
                      help many couples achieve pregnancy within 4 cycles. Your
                      journey requires patience, open communication, and mutual
                      trust. Below are our average success rates per cycle by
                      treatment type.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-760">
                <div className="flex items-center gap-2 mb-6" data-id="element-761">
                  <ActivityIcon className="text-cyan-600" size={20} data-id="element-762" />
                  <h4 className="text-xl font-bold text-slate-900" data-id="element-763">
                    Success Rates by Type of Cycle
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-764">
                  {cycleTypeData.map((item, index) => <div key={index} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-slate-100" data-id="element-765">
                      <div className="text-center mb-4" data-id="element-766">
                        <div className={`inline-flex bg-gradient-to-r ${item.color} p-3 rounded-xl mb-4`} data-id="element-767">
                          <TrendingUpIcon className="text-white" size={24} data-id="element-768" />
                        </div>
                        <h5 className="text-lg font-semibold text-slate-700 mb-2" data-id="element-769">
                          {item.type}
                        </h5>
                        <div className="text-5xl font-bold text-slate-900 mb-2" data-id="element-770">
                          {item.rate}%
                        </div>
                        <p className="text-sm text-slate-600" data-id="element-771">
                          Average success rate per cycle
                        </p>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden" data-id="element-772">
                        <div className={`bg-gradient-to-r ${item.color} h-full rounded-full transition-all duration-1000 ease-out`} style={{
                      width: `${item.rate}%`
                    }} data-id="element-773"></div>
                      </div>
                    </div>)}
                </div>
              </div>
              {/* CTA */}
              <div className="text-center pt-8 border-t border-slate-100" data-id="element-774">
                <a href="/contact" className="inline-block bg-cyan-600 text-white px-8 py-4 rounded-full hover:bg-cyan-700 transition-all duration-300 shadow-lg hover:shadow-cyan-600/30 hover:scale-105 font-semibold" data-id="element-775">
                  Book a Consultation
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* Disclaimer Banner - Moved to bottom */}
        <div className="max-w-4xl mx-auto mt-12" data-id="element-776">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-id="element-777">
            <p className="text-amber-900 text-sm leading-relaxed" data-id="element-778">
              <strong data-id="element-779">Important:</strong> The success rates displayed above are
              provided by our independent partner fertility clinics and
              represent their historical clinical data. These statistics are for
              informational purposes only and do not guarantee individual
              results. Success rates vary significantly based on individual
              factors including age, medical history, and specific conditions.
              Nura Health is a coordination service and does not provide medical
              treatments. Please consult with qualified medical professionals at
              our partner clinics for personalized assessments.
            </p>
          </div>
        </div>
      </div>
    </section>;
}